/* eslint-disable no-prototype-builtins */
import qs from 'qs';
import * as filterStatics from '../../../console/_statics/filter.statics.json';
import { withExpandedProductKeys } from '../../../utility/filter.expansions';

// @ts-expect-error TS(2339): Property 'default' does not exist on type '{ opera... Remove this comment to see the full error message
const stringOperators = filterStatics.default.operators.string;

export function stringifyQuery(query: any, cb: any) {
  const nextQuery = query.reduce(
    (acc: any, { column, operator, value }: any) => {
      if (!column || !operator || !value) {
        return acc;
      }

      // Encode the value if the column's operator is a string-type one -- but element by element
      // where there are several.
      //
      // transformQueryParams hands a multi-value column its selections as an array, and
      // encodeURIComponent takes a string: given ['PLM-S', 'CIC-S'] it coerces to "PLM-S,CIC-S"
      // first and returns "PLM-S%2CCIC-S", which the listing reads as one prefix and matches
      // nothing. `list` columns never hit this because their operators (in/ni) are not string
      // operators; `prefixSelect` uses `sw`, which is, so choosing two carriers -- the reason
      // that type exists -- returned no rows.
      //
      // Mapping keeps each value escaped while leaving the separators to qs, whose
      // arrayFormat: 'comma' joins them. A single value is still a string and is unaffected, so
      // "Acme, Inc" on a free-text column stays one encoded value.
      const encodeIfString = (entry: any) =>
        stringOperators.hasOwnProperty(operator) ? encodeURIComponent(entry) : entry;
      const formattedValue = Array.isArray(value)
        ? value.map(encodeIfString)
        : encodeIfString(value);

      return {
        ...acc,
        f: {
          ...acc.f,
          [column]: {
            ...acc.f[column],
            [operator]: formattedValue,
          },
        },
      };
    },
    { f: {} }
  );

  if (typeof cb === 'function') {
    return cb(nextQuery);
  }

  return qs.stringify(nextQuery, { encode: false, arrayFormat: 'comma' });
}

export const flattenQuery = (query: any) => {
  // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
  const queryString = typeof query === 'string' ? query : stringifyQuery(query);
  const params = queryString.split('&').reduce((acc: any, param: any) => {
    const [key, value] = param.split('=');
    return { ...acc, [key]: value };
  }, {});

  // Every filter that reaches the url passes through here -- the modal, the chip
  // row and both saved-filter menus -- so this is the one place that can widen a
  // stored filter naming half of a product key group. See filter.expansions.
  return withExpandedProductKeys(params);
};

/**
 * The operator a freshly-chosen column should start on.
 *
 * <p>Choosing a column used to blank the operator, which left the Value control greyed out with
 * nothing saying that a second, separate choice was needed first. On a list column — where there
 * are only two operators and "includes" is what anybody wants — that dead end was most of the
 * interaction.
 *
 * <p>The first operator of the type, because `filter.statics.json` already lists them in the order
 * they are offered and the first is the common case for every type: contains for a string, equals
 * for a number, includes for a list.
 *
 * <p>Null for a type nothing is known about, rather than a guessed operator: a filter that silently
 * applies an operator the user did not choose is worse than one that asks.
 */
export const defaultOperatorFor = (type?: string | null): string | null => {
  const operators =
    (filterStatics as any).default?.operators ??
    (filterStatics as any).operators;
  return Object.keys(operators?.[type ?? ''] ?? {})[0] ?? null;
};

/**
 * The line box of a filter control's text, as MUI sizes it.
 *
 * <p>Relative, so it tracks the font — which is the part that has to scale, because the console
 * pins its root font to 12px and re-bases it at 1620 and 1920.
 */
const CONTROL_LINE_BOX = '1.4375em';

/**
 * The fixed chrome around that line box: MUI's 11px of vertical padding top and bottom, plus its
 * 1px border on each edge.
 *
 * <p>In pixels because MUI defines it in pixels and does not scale it with the font — measured at
 * 11px padding and 1px border at both a 12px and a 16px root. It is not a size chosen here; it is
 * the neighbouring inputs' own chrome, mirrored so the two agree.
 */
const CONTROL_CHROME = '24px';

/**
 * The height of a filter row's controls.
 *
 * <p>The column and operator controls are MUI inputs and get this for free. The value control for
 * a list is a button, which has nothing to derive a height from, so it has to be told — and every
 * simpler way of telling it was measured and found wrong:
 *
 * <ul>
 *   <li>a fixed {@code 40px} was 1.25px short at a 12px root and 7px short at a 16px one;</li>
 *   <li>a pure {@code 3.4375rem} matched at 1440 and ran 8px tall at 1920, because rem scales
 *       linearly and the input's height does not;</li>
 *   <li>{@code height: 100%} does not resolve — the grid item has no height of its own — and
 *       collapsing the button's padding to let the row size it left it 5px short.</li>
 * </ul>
 *
 * <p>So it mirrors the formula the inputs themselves follow. Verified equal to them at 1440, 1700
 * and 1920, which is every width where the root font changes.
 */
export const FILTER_CONTROL_HEIGHT = `calc(${CONTROL_LINE_BOX} + ${CONTROL_CHROME})`;
